// dependencies
import React, { useEffect, useState } from "react";

import Text from "../../../../../../components/Text";
// components
import { CardBody, CardHeader } from "../../../../components/Card";
import CohortSelect from "../../../../components/CohortSelect";
import { ILegendItem } from "../../../../components/CohortSelect";
import FactorsPolygonChart from "../../../../components/FactorsPolygonChart";
import useCohortFactorsPolygonChart from "../../../../components/FactorsPolygonChart/useFactorsPolygonChart";

// store
import useStore from "../../../../store";

// hooks
import { useCohorts, usePageSettings, useUpdatePageSettings } from "../../../../../../api/hooks";

// schema
import { ICohortModel } from "../../../../../../api/services";

// utils
import { getColor } from "../../../../../../utils/getColor";
// config
import { Hex, chooseBestContrastingColor } from "../../../../../../utils/getContrast";

// styles
import css from "./SatisfactionFactors.module.scss";

// constants
const MAX_COHORTS = 3;

const SatisfactionFactors = () => {
	const [selectedCohorts, setSelectedCohorts] = useState<ICohortModel[]>([]);
	const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);
	const [filter, month, cohort, setCohort] = useStore((state) => [
		state.filter,
		state.factors.month,
		state.factors.cohort,
		state.factors.setCohort,
	]);

	// API hooks
	const { isLoading: isCohortsLoading, data: cohortsData } = useCohorts();

	const pageSettingsOnSuccess = (onSuccessData) => {
		const newSelectedCohortIds = onSuccessData?.factors?.polygon?.selected_cohorts || [];
		const newSelectedCohorts = cohortsData?.cohorts.filter((c) => newSelectedCohortIds.includes(c._id)) || [];
		setSelectedCohorts(newSelectedCohorts);
		setSelectedCohortIds(newSelectedCohortIds);
	};

	const setPageSettings = useUpdatePageSettings(pageSettingsOnSuccess).mutate;

	// state for selected cohorts
	const {
		data: pageSettingsData,
		refetch: refetchPageSettings,
		isLoading: isPageSettingsLoading,
		isSuccess,
	} = usePageSettings();

	useEffect(() => {
		if (isSuccess) {
			pageSettingsOnSuccess(pageSettingsData);
		}
	}, [isPageSettingsLoading]);

	// refetch page settings when cohorts have loaded
	useEffect(() => {
		if (!isCohortsLoading) {
			refetchPageSettings();
		}
	}, [isCohortsLoading]);

	const { orderedDataSets, isLoadingData, hasError } = useCohortFactorsPolygonChart({
		filter,
		month: month || undefined,
		selectedCohorts,
		cohort,
	});

	// handle selection of cohorts
	const handleAddCohort = (cohort: string) => {
		if (selectedCohorts.length < MAX_COHORTS) {
			setPageSettings({
				settings_path: "factors.polygon.selected_cohorts",
				settings_value: [...selectedCohortIds, cohort],
			});
		}
	};

	// handle removal of cohort
	const handleCohortRemove = (cohortID: string) => {
		setPageSettings({
			settings_path: "factors.polygon.selected_cohorts",
			settings_value: selectedCohortIds.filter((c) => c !== cohortID),
		});
		if (cohortID === cohort) {
			setCohort(null);
		}
	};

	const handleCohortSelect = (cohortID: string) => {
		if (cohortID === cohort) {
			setCohort(null);
		} else {
			setCohort(cohortID);
		}
	};

	const legendItems: ILegendItem[] = [
		{
			id: "all",
			label: "All Employees",
			color: chooseBestContrastingColor({ background: getColor("primary", 5) as Hex, standard: 2.5 }),
			backgroundColor: getColor("primary", 5) as Hex,
		},
		...selectedCohorts.map((cohort) => ({
			id: cohort._id,
			label: cohort.name,
			color: chooseBestContrastingColor({ background: cohort.color as Hex, standard: 2.5 }),
			backgroundColor: cohort.color as Hex,
		})),
	];

	/**
	 * If the sum of all averages is 0, we do not have enough data to display
	 */
	const valuesTotal = orderedDataSets.reduce((acc, dataset) => {
		const value = dataset.data.reduce((value, acc) => {
			return acc + value;
		}, 0);
		return acc + value;
	}, 0);

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<CardHeader
				title={
					<Text element="span" size="paragraph-2" weight="semi-bold">
						Satisfaction Factors
					</Text>
				}
				info="Displays the factors that contribute to the overall satisfaction for the selected cohorts."
			/>
			<CardBody>
				<div className={css.chartContainer}>
					<div className={css.factorsPolygon}>
						<FactorsPolygonChart
							orderedDataSets={orderedDataSets}
							isLoadingData={isLoadingData}
							hasError={hasError}
							selectedCohort={cohort}
						/>
					</div>
					{!isLoadingData && !hasError && valuesTotal ? (
						<CohortSelect
							selectedCohorts={selectedCohorts}
							handleAddCohort={handleAddCohort}
							handleCohortRemove={handleCohortRemove}
							handleCohortSelect={handleCohortSelect}
							legendItems={legendItems}
							maxCohorts={MAX_COHORTS}
							cohort={cohort}
						/>
					) : null}
				</div>
			</CardBody>
		</div>
	);
};

export default SatisfactionFactors;
